import { CampaignEmployee } from '@models/Campaign'
import { campaignEmployeeSchema } from '@models/schema'
import { createSelector, createSlice } from '@reduxjs/toolkit'
import {
  createCampaignEmployee as createCampaignEmployeeAPI,
  deleteCampaignEmployee as deleteCampaignEmployeeAPI,
  fetchCampaignEmployeeById,
  fetchCampaignEmployees,
  updateCampaignEmployee as updateCampaignEmployeeAPI,
} from '@services/CampaignEmployeeService'
import {
  baseReducers,
  getUUIDOrModelUUID,
  SBAPI,
  SBSelectRaw,
  SBStateUUID,
} from '@supplement-bacon/react-toolkit/utils'
import { initialQuery, TableParams } from '@supplement-bacon/sb-table-filters'
import { AppDispatch, RootState } from './store'

const initialState: SBStateUUID<CampaignEmployee> = {
  isLoading: false,
  error: null,
  items: {},
  ids: [],
  selectedId: undefined,
  query: initialQuery,
}

const slice = createSlice({
  name: 'campaignEmployee',
  initialState,
  reducers: baseReducers,
})

// Reducer
export default slice.reducer
export const {
  getItemsSuccess: getCampaignEmployeesSuccess,
  setQuery: setCampaignEmployeeQuery,
  reset: resetCampaignEmployeeState,
  resetQueryAndIds: resetCampaignEmployeeQueryAndIds,
} = slice.actions

/**
 * Selectors
 */

const selectRawItems: SBSelectRaw<
  RootState,
  { [key: string]: CampaignEmployee }
> = (state: RootState) => state[slice.name].items
const selectRawIds: SBSelectRaw<RootState, string[]> = (state: RootState) =>
  state[slice.name].ids
const selectRawSelectedId: SBSelectRaw<RootState, string | undefined> = (
  state: RootState
) => state[slice.name].selectedId

export const selectCampaignEmployees = () =>
  createSelector(
    [selectRawItems, selectRawIds],
    (items, ids) => ids.map((id) => items[id]).filter((i) => i)
    // Filter allow to return only non-null elements
  )
export const selectSelectedCampaignEmployee = () =>
  createSelector([selectRawItems, selectRawSelectedId], (items, id) =>
    id !== undefined ? items[id] : undefined
  )
export const selectCampaignEmployeeById = (id: number) =>
  createSelector([selectRawItems], (items) =>
    items.hasOwnProperty(id) ? items[id] : undefined
  )
export const selectCampaignEmployeesByIds = (ids: number[]) =>
  createSelector([selectRawItems], (items) =>
    ids.filter((id) => items.hasOwnProperty(id)).map((id) => items[id])
  )

/**
 * Actions
 */

export const setSelectedCampaignEmployee =
  (campaignEmployee: CampaignEmployee | string) =>
  async (dispatch: AppDispatch) =>
    dispatch(
      slice.actions.setSelectedId(
        getUUIDOrModelUUID<CampaignEmployee>(campaignEmployee)
      )
    )

export const getCampaignEmployees = (params: TableParams) =>
  SBAPI.FetchPaginatedDispatched(
    fetchCampaignEmployees(params),
    [campaignEmployeeSchema],
    slice.actions
  )

export const createCampaignEmployee = (campaignEmployee: CampaignEmployee) =>
  SBAPI.CreateDispatched(
    createCampaignEmployeeAPI(campaignEmployee),
    campaignEmployeeSchema,
    slice.actions
  )

export const getCampaignEmployeeWithId = (id: number) =>
  SBAPI.FetchDispatched(
    fetchCampaignEmployeeById(id),
    campaignEmployeeSchema,
    slice.actions
  )

export const updateCampaignEmployee = (campaignEmployee: CampaignEmployee) =>
  SBAPI.UpdateDispatched<CampaignEmployee>(
    updateCampaignEmployeeAPI(campaignEmployee),
    campaignEmployee,
    slice.actions
  )

export const deleteCampaignEmployee = (campaignEmployee: CampaignEmployee) =>
  SBAPI.DestroyDispatched<CampaignEmployee>(
    deleteCampaignEmployeeAPI(campaignEmployee),
    campaignEmployee,
    slice.actions
  )
